import { useTranslation } from 'react-i18next';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeft01Icon, InternetIcon } from '@hugeicons/core-free-icons';
import { Button } from '@components/Button/Button';
import { useLanguage } from '@hooks/useLanguage';
import { RtlBackIcon } from '@utils/rtlIcons';
import heroImage from '../public/images/hero-image.webp';

export interface AuthSplitLayoutProps {
  /** Show Back button and call onBack when clicked */
  showBack?: boolean;
  onBack?: () => void;
  /** Left panel: form/content */
  children: React.ReactNode;
  /** Right panel image (default: hero-image; use image_12.png for percentage graphics when available) */
  rightImage?: string;
  /** When false, only the left panel is rendered (e.g. when hero is static in parent). */
  showRightPanel?: boolean;
}

export function AuthSplitLayout({
  showBack = false,
  onBack,
  children,
  rightImage = heroImage,
  showRightPanel = true,
}: AuthSplitLayoutProps) {
  const { t } = useTranslation();
  const { language, toggleLanguage } = useLanguage();
  const languageLabel = language === 'ar' ? t('common.english') : t('common.arabic');

  return (
    <div className="min-h-screen bg-surface-web flex flex-col items-center justify-center p-0 box-border font-sans">
      <div className={`w-full max-w-7xl mx-auto flex flex-col items-center justify-center gap-6 ps-8 pe-8 box-border min-h-screen py-6 max-[767px]:py-6 max-[767px]:ps-4 max-[767px]:pe-4 ${showRightPanel ? 'lg:flex-row lg:items-start lg:py-6' : ''}`}>
        <div className="flex w-full max-w-2xl flex-1 min-h-0 flex-col rounded-2xl overflow-visible border-none bg-transparent lg:w-[646px] lg:min-h-[520px] lg:self-stretch max-[767px]:min-h-[400px] max-[767px]:w-full max-[767px]:max-w-2xl">
          <div className="flex shrink-0 w-full items-center justify-between gap-2 py-2">
            {showBack && onBack ? (
              <Button
                variant="ghost"
                size="md"
                className="h-14 p-4 rounded-[16px]"
                onClick={onBack}
                icon={<RtlBackIcon><HugeiconsIcon icon={ArrowLeft01Icon} size={20} /></RtlBackIcon>}
                iconPosition="left"
              >
                {t('common.back')}
              </Button>
            ) : (
              <span />
            )}
            <Button
              variant="ghost"
              size="md"
              className="h-14 p-4 rounded-[16px] shrink-0"
              icon={<HugeiconsIcon icon={InternetIcon} size={20} />}
              iconPosition="right"
              aria-label={languageLabel}
              onClick={toggleLanguage}
            >
              {languageLabel}
            </Button>
          </div>
          <div className="mt-6 flex flex-1 min-h-0 w-full items-center justify-center px-0">
            {children}
          </div>
          <footer className="shrink-0 py-2 text-center font-sans text-base font-normal leading-snug text-primary/70">
            {t('auth.footer')}
          </footer>
        </div>
        {/* Right: Percentage graphics (only when showRightPanel) */}
        {showRightPanel && (
          <div
            className="hidden w-[646px] min-h-[520px] shrink-0 rounded-2xl overflow-hidden lg:block lg:self-stretch xl:min-w-0 xl:flex-1"
            aria-hidden
          >
            <img
              src={rightImage}
              alt=""
              width={646}
              height={520}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover object-center block"
              aria-hidden
            />
          </div>
        )}
      </div>
    </div>
  );
}
